import { Controller } from "@hotwired/stimulus"

// Drives the "Criar com IA" modal on the Feature editor pages. The flow:
//
//   1. button click -> #open() shows the <dialog>
//   2. user fills instructions + locales, submits the form
//   3. #submit() POSTs to /admin/features/ai_suggest via fetch
//   4. response (an HTML partial) replaces the modal's "result" target
//   5. user clicks "Usar este conteúdo" inside a result card ->
//      #apply() copies the suggested name + description into the
//      real translation form fields for that locale and closes the modal
//
// We don't use Turbo Streams because the suggestion result is purely
// modal-local — it never persists or replaces anything outside the
// dialog.
export default class extends Controller {
  static targets = ["modal", "form", "instructions", "status", "result", "submitBtn", "manualSelect"]

  // How long after a successful response to keep the submit button
  // disabled. Guards against a rapid double-click where the second
  // click's `submitting` check races with the first response landing.
  static COOLDOWN_MS = 500

  connect() {
    // Bind once so submit handler can be added/removed cleanly.
    this._onSubmit = this.submit.bind(this)
    if (this.hasFormTarget) {
      this.formTarget.addEventListener("submit", this._onSubmit)
    }
    this._abortController = null
    this._cooldownTimer = null
  }

  disconnect() {
    if (this.hasFormTarget) {
      this.formTarget.removeEventListener("submit", this._onSubmit)
    }
    if (this._abortController) this._abortController.abort()
    if (this._cooldownTimer) clearTimeout(this._cooldownTimer)
  }

  open(event) {
    event?.preventDefault?.()
    this.clearResult()
    if (this.hasModalTarget && typeof this.modalTarget.showModal === "function") {
      this.modalTarget.showModal()
    }
    // Pre-select the owner manual matching the car the user picked in
    // the surrounding feature form — Bruno's "adicionar no prompt
    // inicial o manual do carro selecionado". The dropdown still lets
    // the user override to a different car's manual or none.
    this._syncManualToCarSelection()
    queueMicrotask(() => this.instructionsTarget?.focus())
  }

  _syncManualToCarSelection() {
    if (!this.hasManualSelectTarget) return
    const carSelect = document.getElementById("feature_car_id")
    const carId = carSelect && carSelect.value
    if (!carId) return

    let firstMatch = null
    Array.from(this.manualSelectTarget.options).forEach((opt) => {
      if (!opt.value) return // skip the blank "— não incluir —" option
      const matches = opt.dataset.carId === carId
      opt.hidden = !matches
      if (matches && !firstMatch) firstMatch = opt
    })
    if (firstMatch) this.manualSelectTarget.value = firstMatch.value
  }

  close(event) {
    event?.preventDefault?.()
    if (this._abortController) this._abortController.abort()
    if (this.hasModalTarget) this.modalTarget.close()
  }

  async submit(event) {
    event.preventDefault()
    // Two-layer guard: the `submitting` flag blocks re-entry while the
    // request is in flight, and the cooldown timer keeps the button
    // disabled for COOLDOWN_MS after the response so a double-click
    // landing right at the response boundary can't fire twice.
    if (this.submitting || this._cooldownTimer) return
    this.submitting = true
    this.toggleBusy(true)
    this.clearResult()

    // Cancel any previously in-flight fetch (shouldn't happen given the
    // flag above, but if it does — e.g. Turbo Drive sneaking a parallel
    // submit through — we'd rather abort the old one than race them).
    if (this._abortController) this._abortController.abort()
    this._abortController = new AbortController()

    const form = this.formTarget
    const formData = new FormData(form)

    try {
      const response = await fetch(form.action, {
        method: "POST",
        body: formData,
        headers: { "Accept": "text/html", "X-Requested-With": "XMLHttpRequest" },
        credentials: "same-origin",
        signal: this._abortController.signal
      })
      const html = await response.text()
      this.resultTarget.innerHTML = html
    } catch (err) {
      if (err.name === "AbortError") return // cancelled — leave result blank
      this.resultTarget.innerHTML = `<div class="alert alert-error text-sm">${this.escape(err.message)}</div>`
    } finally {
      this.toggleBusy(false)
      this.submitting = false
      this._abortController = null
      // Cooldown: keep the button disabled briefly so a misfired double
      // click can't re-submit instantly.
      if (this.hasSubmitBtnTarget) this.submitBtnTarget.disabled = true
      this._cooldownTimer = setTimeout(() => {
        this._cooldownTimer = null
        if (this.hasSubmitBtnTarget) this.submitBtnTarget.disabled = false
      }, this.constructor.COOLDOWN_MS)
    }
  }

  // Click handler on each per-locale "Usar este conteúdo" button.
  // Reaches outside the modal to hydrate the matching translation
  // inputs on the underlying form. Name field is a plain text input
  // (name="translations[<loc>][name]"); description is a Lexical-backed
  // hidden input (id="feat_desc_<loc>") whose value the Lexical editor
  // mirrors automatically — we hand the HTML to the editor's load()
  // method so the contenteditable surface re-renders.
  apply(event) {
    const { locale, name, description } = event.params
    if (!locale) return
    this._applyOne(locale, name, description)
    this.close()
  }

  // Click handler on the "Usar ambos os idiomas" footer button. Reads
  // every per-locale result card the server rendered into the result
  // pane and hydrates each underlying field in turn before closing.
  // Same per-locale code path as #apply — we just call it once per
  // card so the Trix editors get their loadHTML in sequence.
  applyBoth(event) {
    event?.preventDefault?.()
    if (!this.hasResultTarget) return

    const cards = this.resultTarget.querySelectorAll("[data-ai-locale-card]")
    cards.forEach((card) => {
      const locale = card.dataset.locale
      const name = card.dataset.name
      const description = card.dataset.description
      if (locale) this._applyOne(locale, name, description)
    })
    this.close()
  }

  _applyOne(locale, name, description) {
    const nameInput = document.querySelector(
      `input[name="translations[${locale}][name]"]`
    )
    if (nameInput) {
      nameInput.value = name || ""
      nameInput.dispatchEvent(new Event("input", { bubbles: true }))
    }

    const descHidden = document.getElementById(`feat_desc_${locale}`)
    if (descHidden) descHidden.value = description || ""

    // Push the new HTML into the Lexical editor instance bound to this
    // input. The rich_editor partial tags itself with
    // [data-rich-editor-for="<input_id>"] so the lookup is stable
    // regardless of how the editor is positioned in the DOM.
    this._loadLexical(`feat_desc_${locale}`, description || "")
  }

  _loadLexical(inputId, html) {
    const editorEl = document.querySelector(`[data-rich-editor-for="${inputId}"]`)
    if (!editorEl) return
    const ctrl = this.application.getControllerForElementAndIdentifier(editorEl, "lexical-editor")
    if (ctrl && typeof ctrl.load === "function") ctrl.load(html)
  }

  toggleBusy(busy) {
    if (this.hasStatusTarget) this.statusTarget.classList.toggle("hidden", !busy)
    if (this.hasSubmitBtnTarget) this.submitBtnTarget.disabled = busy
  }

  clearResult() {
    if (this.hasResultTarget) this.resultTarget.innerHTML = ""
  }

  escape(str) {
    const div = document.createElement("div")
    div.textContent = str
    return div.innerHTML
  }
}
